// What an ORIGIN requires of the box that runs it — folded in at the one write
// seam, never at the producers. See `../task-envelope.ts` for the file docblock.

import {
  NOT_YET_DERIVED_PROVISIONS,
  RunnerCapability,
  type RunnerRole,
  runnerRoleCanConverse,
} from '../../entities/runner';
import { OutcomeKind } from '../../entities/task/outcome';
import type { TaskEnvelope } from './envelope';
import { TaskOrigin } from './origin';

// ─── origin-derived capability requirements (#1505) ──────────────────────────
//
// Some requirements are a property of WHAT THE TASK IS, not of what its producer
// remembered to ask for. A review always drives the model on a BYOK credential
// SEALED onto the assignment, so it can only run on a box that can unwrap one —
// that is true of every `pr_review` task ever minted, by any producer.
//
// Which is why this is not a line in the review envelope BUILDER. TWO producers
// mint review tasks: `reviewTaskEnvelope` / `reviewReplyTaskEnvelope`
// (`@developerz/review`, the webhook path) and `POST /v1/tasks`, whose body schema
// lets a caller set `origin` and leaves `requires` optional. A rule enforced in the
// builder is a rule the REST producer walks straight past, minting an unfenced
// review task that lands on a box which dies at its config seam a second later.
// One seam per invariant: this is the rule, and `@developerz/db`'s task-write path
// is where it is applied.

/**
 * The origins that name an INTERACTIVE, mailbox-holding session rather than
 * autonomous work — the TASK side of the claim gate, and the enumeration every
 * other statement about "interactive origins" in this repo is built from.
 *
 * It is exactly {@link TaskOrigin.Scout} and {@link TaskOrigin.Setup}, because
 * those are exactly the two origins `interactiveRoleFor`
 * (`apps/runner/src/modes/router.ts`) maps to a conversational executor. Every
 * other origin — `pr_review`, `pr_review_reply`, `pr_babysit`, `devops`, and the
 * coding fall-through — reaches `routeAutonomous`. `devops` is the one that looks
 * like it belongs here and does not: it reports and decides but never converses,
 * so it holds no mailbox.
 *
 * Declared in the DOMAIN and not in `apps/runner` beside that router, because the
 * DISPATCH side has to answer the same question before it hands a box the work,
 * and a package cannot import an app. Two spellings of "which origins are
 * interactive" is how the two sides come to disagree about a task already in
 * flight.
 */
export const INTERACTIVE_TASK_ORIGINS: readonly TaskOrigin[] = [TaskOrigin.Scout, TaskOrigin.Setup];

/**
 * Does a task of this origin need a box that can hold a MAILBOX and converse?
 * True for exactly {@link INTERACTIVE_TASK_ORIGINS}. Pure.
 */
export function originRequiresConversationalBox(origin: TaskOrigin): boolean {
  return INTERACTIVE_TASK_ORIGINS.includes(origin);
}

/**
 * MAY A BOX PINNED TO THIS ROLE EXECUTE A TASK OF THIS ORIGIN? The one seam that
 * composes the pair, so no caller has to remember that there are two halves.
 *
 * The gate was one-directional for the whole life of the fleet, and both halves
 * existed the entire time — the coding half (the since-deleted `runnerRoleCanCode`)
 * was enforced at the claim (`listAssignableRunners`, `claimAndOutbox`) and
 * {@link runnerRoleCanConverse} was asked only by `plan_start`, which counts capacity and places nothing. So the
 * platform verified that an account HAD a conversational box and then handed the
 * session to whichever box the claim ordering reached first.
 *
 * MEASURED, not reasoned (prod, 2026-08-26). Of 489 `origin: 'scout'` tasks, the
 * 52 that landed on a `worker`-pinned box failed 39 times — 75% — against 1
 * failure in 438 on a `universal` box. A pinned `worker` never reads
 * `isInteractiveAssignment`, so it falls through to `routeAutonomous`, whose
 * fall-through is the PR-opening coding router: the box ran a CODING adapter
 * against a PLANNING prompt, and could open a pull request for a planning session.
 *
 * An interactive origin asks the converse question and NOTHING ELSE; every other origin
 * is coding work, which every role can run (`worker` and `universal` both code), so it
 * admits any role. Which half applies is a property of the ORIGIN; only then is the
 * role asked.
 */
export function runnerRoleCanRunOrigin(role: RunnerRole, origin: TaskOrigin): boolean {
  return originRequiresConversationalBox(origin) ? runnerRoleCanConverse(role) : true;
}

/**
 * The capability tokens a task of this origin ALWAYS requires, whatever its
 * producer supplied. Empty for every other origin — the common path is untouched.
 *
 * Three lanes, each for its own reason:
 *
 *   - the REVIEW lane (`pr_review`, `pr_review_reply`) requires `secrets:sealed`. Every
 *     task carrying a sealed credential has the same hazard, coding included, but
 *     requiring the token fleet-wide would stop every box on an older build, in every
 *     tenant, from claiming ANY work — a per-task failure traded for a fleet-wide stall.
 *     Widening it is its own change.
 *   - the MODEL-CALL lane (`triage_judge`, `kb_fold`, #6759) requires
 *     `harness:model-call`, which the platform derives only for a build that can answer
 *     an offer (`RUNNER_MODEL_CALL_MIN_VERSION`, #6760). That is what keeps an older
 *     box from ever being handed one, so it never has to decode the origin.
 *   - the PLANNER lane (`plan_author`, #7569) requires `harness:model-call` AND
 *     `harness:planner`. The model-call token alone is not enough: a plan call's schema
 *     key is learned only from a build (`@developerz/agent` `model-call/schemas.ts`), so
 *     only a build that derives `harness:planner` (#6765) may be offered one.
 *   - the SCOUT lane requires `harness:planner` (#6764, owner ruling R1: "only runners that
 *     report the new Planner capability may claim scout tasks"). Every scout assignment
 *     carries `planner_handoff` (`buildAssignment`, `apps/worker`), so a scout must run on a
 *     build that hands a doable ask to the Planner instead of planning it on the box. The
 *     token is derived only from a REPORTED build at or above `RUNNER_PLANNER_MIN_VERSION`
 *     (`boxPlannerCapable`), so the gate is per box, at runtime (owner ruling 2026-11-01): a
 *     box below it claims no scout task, and Scout has no cluster path, so such a task is
 *     held `no_capable_box` until a capable box is online.
 *
 * A switch with no default, so a new origin is a type error here until it states its
 * requirement. The claim's SQL CASE is GENERATED from this function
 * (`@developerz/db` `queries/tasks/gates.ts`), never written by hand.
 */
export function requiredCapabilitiesForOrigin(origin: TaskOrigin): readonly string[] {
  switch (origin) {
    case TaskOrigin.PrReview:
    case TaskOrigin.PrReviewReply:
      return [RunnerCapability.SealedSecrets];
    case TaskOrigin.TriageJudge:
    case TaskOrigin.KbFold:
      return [RunnerCapability.ModelCall];
    case TaskOrigin.PlanAuthor:
      return [RunnerCapability.ModelCall, RunnerCapability.Planner];
    case TaskOrigin.Scout:
      return [RunnerCapability.Planner];
    case TaskOrigin.Conversation:
    case TaskOrigin.GhIssue:
    case TaskOrigin.Sentry:
    case TaskOrigin.Linear:
    case TaskOrigin.Manual:
    case TaskOrigin.PrBabysit:
    case TaskOrigin.PrBabysitWatch:
    case TaskOrigin.Setup:
    case TaskOrigin.Devops:
      return [];
  }
}

/**
 * The capability tokens a task of this OUTCOME KIND always requires, whatever its origin
 * and its producer supplied — the outcome-kind twin of {@link requiredCapabilitiesForOrigin}.
 *
 * One kind requires one today: `live_check` requires `harness:live-check` (plan
 * 101-multi-repo-goal-legs D6). The box decodes `outcome_kind` against a CLOSED enum, so a
 * build that predates the member refuses the whole assignment; the token is derived only
 * from a build that decodes it (`boxLiveCheckCapable`), so no older box is ever offered one.
 * The kind is a COLUMN (`tasks.outcome_kind`), not an envelope field, so the claim reads it
 * at claim time (`outcomeKindRequiresCase`, `@developerz/db`), GENERATED from this function.
 *
 * A switch with no default, so a new outcome kind is a type error here until it says what
 * it requires.
 */
export function requiredCapabilitiesForOutcomeKind(kind: OutcomeKind): readonly string[] {
  switch (kind) {
    case OutcomeKind.LiveCheck:
      return [RunnerCapability.LiveCheckLane];
    case OutcomeKind.Pr:
    case OutcomeKind.PrUnfulfilled:
    case OutcomeKind.Report:
    case OutcomeKind.OpsAction:
      return [];
  }
}

/**
 * Can ANY machine claim a task of this origin today? False exactly when the origin requires
 * a token no build is derived yet ({@link NOT_YET_DERIVED_PROVISIONS}): none today, since
 * #6765 derives `harness:planner` (`plan_author` was, until it; the model-call origins were,
 * until #6760). A surface that
 * names a machine's possible roles, or an agent's work, reads this, so it never offers a role
 * no claim can give.
 */
export function originClaimableToday(origin: TaskOrigin): boolean {
  return !requiredCapabilitiesForOrigin(origin).some((t) => NOT_YET_DERIVED_PROVISIONS.includes(t));
}

/**
 * Fold {@link requiredCapabilitiesForOrigin} into an envelope's own `requires`.
 * Union, never replace — a producer's tokens are additional needs, not an
 * alternative to this one. Idempotent, and returns the envelope UNCHANGED (same
 * reference) when there is nothing to add, so an origin with no derived
 * requirement stays byte-identical and a redelivery cannot produce a different
 * stored envelope for identical input. Pure.
 */
export function withRequiredCapabilities(envelope: TaskEnvelope): TaskEnvelope {
  const derived = requiredCapabilitiesForOrigin(envelope.origin);
  if (derived.length === 0) return envelope;
  const declared = envelope.requires ?? [];
  // **The reserved token is normalized on BOTH sides, and merely FOLDING here was not
  // enough — it was the bug.** The read side is an EXACT match (`capabilitiesSatisfy`
  // is `Set.has`) and boxes only ever provision the lowercase constant. So a REST
  // caller posting `SECRETS:SEALED` on a `pr_review` envelope must not have that
  // spelling survive: a fold that only decides whether to APPEND leaves the caller's
  // variant in place, `every()` fails on it at the claim gate, and the task is
  // unclaimable by every box forever — the exact outcome an earlier draft of this
  // comment claimed the fold prevented. It prevented the duplicate, not the strand.
  // Drop any case-variant of a derived token, keep a canonical one where it already
  // sits, then append whatever is still missing.
  const canonical = new Set<string>(derived);
  const derivedFolded = new Set(derived.map((t) => t.toLowerCase()));
  const kept = declared.filter((t) => canonical.has(t) || !derivedFolded.has(t.toLowerCase()));
  const missing = derived.filter((token) => !kept.includes(token));
  if (kept.length === declared.length && missing.length === 0) return envelope;
  return { ...envelope, requires: [...kept, ...missing] };
}

/**
 * Is this a MODEL-CALL origin — one offer of one model call to a machine (#6759, #6760)?
 * Derived from the capability the origin requires, never a restated list, so the claim
 * filter, the bound seal and the sweep cannot disagree with `requiredCapabilitiesForOrigin`
 * about which origins are offers.
 */
export function isModelCallOrigin(origin: TaskOrigin): boolean {
  return requiredCapabilitiesForOrigin(origin).includes(RunnerCapability.ModelCall);
}
